import { z } from "zod";
import { dietSchema } from "./enums";
import { MAX_ITEM_SUGGESTIONS, publicOfferSchema } from "./offers";
import { timeOfDaySchema } from "./restaurant";

/**
 * The menu: categories, items, variants, add-on groups, tax classes and
 * kitchen stations. See docs/architecture/data-model.md, Catalog.
 */

/** Tags the owner can put on an item. Clients translate them. */
export const ITEM_TAGS = ["bestseller", "new", "chefs_special", "spicy"] as const;
export const itemTagSchema = z.enum(ITEM_TAGS);
export type ItemTag = z.infer<typeof itemTagSchema>;

/** Every restaurant starts with this tax class, as its default (BL-04). */
export const DEFAULT_TAX_CLASS = { name: "GST 5%", rateBp: 500 } as const;

/** Every restaurant starts with this kitchen station. */
export const DEFAULT_KITCHEN_STATION = { name: "Kitchen" } as const;

/** The highest price the menu accepts: ₹1,00,000. */
export const MAX_PRICE_PAISE = 10_000_000;
export const pricePaiseSchema = z.number().int().min(0).max(MAX_PRICE_PAISE);

/** Basis points: 500 is 5%. */
export const rateBpSchema = z.number().int().min(0).max(10_000);

const name = (max: number) => z.string().trim().min(1).max(max);
/** An optional text field. An empty string clears it. */
const optionalText = (max: number) =>
	z
		.string()
		.trim()
		.max(max)
		.transform((v) => (v === "" ? null : v))
		.nullable();

/** Typed in the item picker: 1 to 8 letters or digits, stored in capitals. */
export const shortCodeSchema = z
	.string()
	.trim()
	.regex(/^[A-Za-z0-9]{1,8}$/, "1 to 8 letters or digits")
	.transform((v) => v.toUpperCase());

const uniqueIds = (ids: string[]) => new Set(ids).size === ids.length;

// ---------------------------------------------------------------------------
// Staff shapes: `staff.catalog.*`. Everything not deleted, including hidden
// and unavailable rows.
// ---------------------------------------------------------------------------

/** An attached photo. Build URLs with `imageSources` from images.ts. */
export const imageRefSchema = z.object({
	id: z.uuid(),
	blurhash: z.string().nullable(),
	/** Present, true, for a photo made by AI (`is_ai_generated`, P8-T20). */
	ai: z.literal(true).optional(),
});
export type ImageRef = z.infer<typeof imageRefSchema>;

export const categorySchema = z.object({
	id: z.uuid(),
	name: z.string(),
	description: z.string().nullable(),
	image: imageRefSchema.nullable(),
	sortOrder: z.number().int(),
	/** False: hidden from the menu with all its items. */
	isActive: z.boolean(),
	/** "HH:MM" in the restaurant's time zone, both or neither, such as a breakfast menu. */
	availableFrom: timeOfDaySchema.nullable(),
	availableTo: timeOfDaySchema.nullable(),
});
export type Category = z.infer<typeof categorySchema>;

export const variantSchema = z.object({
	id: z.uuid(),
	name: z.string(),
	pricePaise: z.number().int(),
	isDefault: z.boolean(),
	isAvailable: z.boolean(),
	sortOrder: z.number().int(),
});
export type Variant = z.infer<typeof variantSchema>;

export const itemSchema = z.object({
	id: z.uuid(),
	categoryId: z.uuid(),
	name: z.string(),
	/** Shorter name for KOTs and bills, or null to print `name`. */
	namePrint: z.string().nullable(),
	description: z.string().nullable(),
	diet: dietSchema,
	/** The price. With variants, the lowest variant price, for "from ₹120". */
	basePricePaise: z.number().int(),
	/** Null: the default tax class. */
	taxClassId: z.uuid().nullable(),
	image: imageRefSchema.nullable(),
	shortCode: z.string().nullable(),
	tags: z.array(itemTagSchema),
	/** Null: the first kitchen station. */
	kitchenStationId: z.uuid().nullable(),
	/** False: sold out for now, shown greyed out. */
	isAvailable: z.boolean(),
	/** False: hidden from the menu. */
	isActive: z.boolean(),
	sortOrder: z.number().int(),
	/** In order. Empty for an item with one price. */
	variants: z.array(variantSchema),
	/** Add-on groups the item offers, in order. */
	modifierGroupIds: z.array(z.uuid()),
	/** "Goes well with": up to four other dishes, in order (P9-T11). */
	suggestedItemIds: z.array(z.uuid()),
});
export type Item = z.infer<typeof itemSchema>;

export const modifierOptionSchema = z.object({
	id: z.uuid(),
	name: z.string(),
	priceDeltaPaise: z.number().int(),
	/** Null: no mark, as for a sauce. */
	diet: dietSchema.nullable(),
	isAvailable: z.boolean(),
	sortOrder: z.number().int(),
});
export type ModifierOption = z.infer<typeof modifierOptionSchema>;

/** An add-on group, shared by the items it is attached to. */
export const modifierGroupSchema = z.object({
	id: z.uuid(),
	name: z.string(),
	/** 0: optional. */
	minSelect: z.number().int(),
	maxSelect: z.number().int(),
	sortOrder: z.number().int(),
	options: z.array(modifierOptionSchema),
});
export type ModifierGroup = z.infer<typeof modifierGroupSchema>;

export const taxClassSchema = z.object({
	id: z.uuid(),
	name: z.string(),
	rateBp: z.number().int(),
	/** Exactly one class is the default. */
	isDefault: z.boolean(),
});
export type TaxClass = z.infer<typeof taxClassSchema>;

export const kitchenStationSchema = z.object({
	id: z.uuid(),
	name: z.string(),
	/** The printer for its KOTs, from Phase 5. */
	printerId: z.uuid().nullable(),
	sortOrder: z.number().int(),
});
export type KitchenStation = z.infer<typeof kitchenStationSchema>;

/**
 * `staff.catalog.listMenu`: the whole menu for the Menu tab, the forms and
 * the item picker. Flat lists in display order: categories and add-on groups
 * by `sortOrder`, items by category and then `sortOrder`, stations by
 * `sortOrder`, tax classes by rate.
 */
export const staffMenuSchema = z.object({
	categories: z.array(categorySchema),
	items: z.array(itemSchema),
	modifierGroups: z.array(modifierGroupSchema),
	taxClasses: z.array(taxClassSchema),
	kitchenStations: z.array(kitchenStationSchema),
	/**
	 * The offers that are on and not over (no coupons), as diners get them, so
	 * the quick bill prices like the server (P9-T04). A menu kept on the
	 * device from before Phase 9 has none.
	 */
	offers: z.array(publicOfferSchema).default([]),
});
export type StaffMenu = z.infer<typeof staffMenuSchema>;

// ---------------------------------------------------------------------------
// Staff inputs. Every create takes a client-made `id` (uuidv7 from ids.ts):
// the same call again updates the row it created and returns it. Optional
// fields that are left out keep their value, or take the default on create.
// ---------------------------------------------------------------------------

export const upsertCategoryInputSchema = z
	.object({
		id: z.uuid(),
		name: name(60),
		description: optionalText(300).optional(),
		/** An uploaded file of purpose `category_image`, or null to remove. */
		imageFileId: z.uuid().nullable().optional(),
		isActive: z.boolean().optional(),
		availableFrom: timeOfDaySchema.nullable().optional(),
		availableTo: timeOfDaySchema.nullable().optional(),
	})
	.refine(
		(v) =>
			(v.availableFrom === undefined) === (v.availableTo === undefined) &&
			(v.availableFrom === null) === (v.availableTo === null),
		{ message: "Give both times or neither", path: ["availableTo"] },
	)
	.refine((v) => v.availableFrom == null || v.availableFrom !== v.availableTo, {
		message: "From and to must differ",
		path: ["availableTo"],
	});
export type UpsertCategoryInput = z.input<typeof upsertCategoryInputSchema>;

export const variantInputSchema = z.object({
	id: z.uuid(),
	name: name(40),
	pricePaise: pricePaiseSchema,
	isDefault: z.boolean().optional(),
	/** Left out: unchanged, or available for a new variant. */
	isAvailable: z.boolean().optional(),
});
export type VariantInput = z.input<typeof variantInputSchema>;

export const upsertItemInputSchema = z.object({
	id: z.uuid(),
	categoryId: z.uuid(),
	name: name(80),
	namePrint: optionalText(40).optional(),
	description: optionalText(500).optional(),
	diet: dietSchema,
	/** Needed for a new item without variants. Ignored when it has variants. */
	basePricePaise: pricePaiseSchema.optional(),
	taxClassId: z.uuid().nullable().optional(),
	/** An uploaded file of purpose `item_image`, or null to remove. */
	imageFileId: z.uuid().nullable().optional(),
	shortCode: shortCodeSchema
		.nullable()
		.or(z.literal("").transform(() => null))
		.optional(),
	tags: z
		.array(itemTagSchema)
		.max(ITEM_TAGS.length)
		.transform((tags) => [...new Set(tags)])
		.optional(),
	kitchenStationId: z.uuid().nullable().optional(),
	isActive: z.boolean().optional(),
	isAvailable: z.boolean().optional(),
	/**
	 * The full list, in order. Variants left out are removed. At most one is
	 * the default; with none marked, the first is. Left out: unchanged.
	 */
	variants: z
		.array(variantInputSchema)
		.max(20)
		.refine((vs) => uniqueIds(vs.map((v) => v.id)), "Variant IDs must differ")
		.refine((vs) => vs.filter((v) => v.isDefault).length <= 1, "At most one default variant")
		.optional(),
	/** The add-on groups, in order. Left out: unchanged. */
	modifierGroupIds: z.array(z.uuid()).max(20).refine(uniqueIds, "Groups must differ").optional(),
	/** Suggested add-ons, other dishes, in order. Left out: unchanged. */
	suggestedItemIds: z
		.array(z.uuid())
		.max(MAX_ITEM_SUGGESTIONS)
		.refine(uniqueIds, "Dishes must differ")
		.optional(),
});
export type UpsertItemInput = z.input<typeof upsertItemInputSchema>;

export const modifierOptionInputSchema = z.object({
	id: z.uuid(),
	name: name(60),
	priceDeltaPaise: pricePaiseSchema,
	diet: dietSchema.nullable().optional(),
	/** Left out: unchanged, or available for a new option. */
	isAvailable: z.boolean().optional(),
});
export type ModifierOptionInput = z.input<typeof modifierOptionInputSchema>;

export const upsertModifierGroupInputSchema = z
	.object({
		id: z.uuid(),
		name: name(60),
		minSelect: z.number().int().min(0).max(30),
		maxSelect: z.number().int().min(1).max(30),
		/** The full list, in order. Options left out are removed. */
		options: z
			.array(modifierOptionInputSchema)
			.min(1)
			.max(30)
			.refine((os) => uniqueIds(os.map((o) => o.id)), "Option IDs must differ"),
	})
	.refine((g) => g.minSelect <= g.maxSelect, {
		message: "Minimum is more than maximum",
		path: ["minSelect"],
	})
	.refine((g) => g.maxSelect <= g.options.length, {
		message: "Maximum is more than the number of options",
		path: ["maxSelect"],
	});
export type UpsertModifierGroupInput = z.input<typeof upsertModifierGroupInputSchema>;

/** Attach an add-on group to exactly these items, from the group's form. */
export const setModifierGroupItemsInputSchema = z.object({
	groupId: z.uuid(),
	itemIds: z.array(z.uuid()).max(1000).refine(uniqueIds, "Items must differ"),
});
export const modifierGroupItemsSchema = z.object({
	groupId: z.uuid(),
	itemIds: z.array(z.uuid()),
});

export const upsertTaxClassInputSchema = z.object({
	id: z.uuid(),
	name: name(40),
	rateBp: rateBpSchema,
	/** True makes this the default instead of the current one. */
	isDefault: z.boolean().optional(),
});
export type UpsertTaxClassInput = z.input<typeof upsertTaxClassInputSchema>;

export const upsertKitchenStationInputSchema = z.object({
	id: z.uuid(),
	name: name(40),
});
export type UpsertKitchenStationInput = z.input<typeof upsertKitchenStationInputSchema>;

const orderedIds = z.array(z.uuid()).min(1).max(1000).refine(uniqueIds, "IDs must differ");

/**
 * New order after a drag. `ids` are placed first, in this order; rows not
 * named keep their order after them. For items, `categoryId` is the category
 * they are dropped into, so an item can move to another category.
 */
export const reorderInputSchema = z.discriminatedUnion("kind", [
	z.object({ kind: z.literal("categories"), ids: orderedIds }),
	z.object({ kind: z.literal("items"), categoryId: z.uuid(), ids: orderedIds }),
	z.object({ kind: z.literal("modifierGroups"), ids: orderedIds }),
	z.object({ kind: z.literal("kitchenStations"), ids: orderedIds }),
]);
export type ReorderInput = z.infer<typeof reorderInputSchema>;

/** The whole list after the move, in order (for items: that category's items). */
export const reorderResultSchema = z.object({ ids: z.array(z.uuid()) });

/** Sold out or back. Two taps from the Menu tab (OP-16). */
export const setAvailabilityInputSchema = z.object({
	kind: z.enum(["item", "variant", "modifierOption"]),
	id: z.uuid(),
	isAvailable: z.boolean(),
});
export type SetAvailabilityInput = z.infer<typeof setAvailabilityInputSchema>;

/** The changed entity: the item for an item or variant, the group for an option. */
export const setAvailabilityResultSchema = z.discriminatedUnion("kind", [
	z.object({ kind: z.literal("item"), item: itemSchema }),
	z.object({ kind: z.literal("modifierGroup"), modifierGroup: modifierGroupSchema }),
]);
export type SetAvailabilityResult = z.infer<typeof setAvailabilityResultSchema>;

export const deleteInputSchema = z.object({ id: z.uuid() });
export const deletedSchema = z.object({ id: z.uuid() });

// ---------------------------------------------------------------------------
// Public shapes: `public.catalog.getMenu`, for the storefront menu, item
// sheet, search and veg filter. Only what a diner sees: hidden and deleted
// rows are left out, sold-out rows are kept with `isAvailable: false`.
// ---------------------------------------------------------------------------

export const publicVariantSchema = variantSchema.omit({ sortOrder: true });
export type PublicVariant = z.infer<typeof publicVariantSchema>;

export const publicItemSchema = z.object({
	id: z.uuid(),
	name: z.string(),
	description: z.string().nullable(),
	diet: dietSchema,
	/** The price, or with variants the lowest one ("from ₹120"). */
	basePricePaise: z.number().int(),
	image: imageRefSchema.nullable(),
	tags: z.array(itemTagSchema),
	/** False: shown greyed out and cannot be added (SF-05). */
	isAvailable: z.boolean(),
	/** In order; the item sheet preselects the default. Empty for one price. */
	variants: z.array(publicVariantSchema),
	/** Keys into `PublicMenu.modifierGroups`, in order. */
	modifierGroupIds: z.array(z.uuid()),
	/** "Goes well with": visible dishes of this menu, in order. */
	suggestedItemIds: z.array(z.uuid()),
});
export type PublicItem = z.infer<typeof publicItemSchema>;

export const publicCategorySchema = z.object({
	id: z.uuid(),
	name: z.string(),
	description: z.string().nullable(),
	image: imageRefSchema.nullable(),
	/** "HH:MM" in the restaurant's time zone, both or neither. */
	availableFrom: timeOfDaySchema.nullable(),
	availableTo: timeOfDaySchema.nullable(),
	/** In menu order. Never empty: categories with no visible items are left out. */
	items: z.array(publicItemSchema),
});
export type PublicCategory = z.infer<typeof publicCategorySchema>;

export const publicModifierOptionSchema = modifierOptionSchema.omit({ sortOrder: true });
export type PublicModifierOption = z.infer<typeof publicModifierOptionSchema>;

export const publicModifierGroupSchema = modifierGroupSchema
	.omit({ sortOrder: true, options: true })
	.extend({ options: z.array(publicModifierOptionSchema) });
export type PublicModifierGroup = z.infer<typeof publicModifierGroupSchema>;

/**
 * `public.catalog.getMenu`: the whole visible menu in one response, so the
 * item sheet opens with no request. Categories and items in menu order;
 * add-on groups once each, shared by the items that name them. The same menu
 * always gives the same bytes: `version` is a hash of the content, and the
 * HTTP response carries an ETag.
 */
/**
 * `public.catalog.getPopular` (P11-T09): the menu items in the most orders
 * over the last 30 days, most first, at most six; empty while the
 * restaurant's `showPopular` is off or too little was ordered.
 */
export const popularItemsSchema = z.object({ itemIds: z.array(z.uuid()) });
export type PopularItems = z.infer<typeof popularItemsSchema>;

export const publicMenuSchema = z.object({
	restaurantId: z.uuid(),
	version: z.string(),
	categories: z.array(publicCategorySchema),
	modifierGroups: z.array(publicModifierGroupSchema),
	/**
	 * Offers that are switched on and not over, with their schedules: which
	 * run now is decided where the menu is shown, with `isOfferLive` from
	 * billing-core, so a cached menu stays right (P9-T06). No coupons.
	 */
	offers: z.array(publicOfferSchema),
});
export type PublicMenu = z.infer<typeof publicMenuSchema>;
